import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import IngestionBanner from "./IngestionBanner";

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  // Phase 6: user is now `{ username, email, role, legacy }` (or null)
  const userEmail = user?.email || user?.username || "—";
  const isAdmin = user?.role === "admin";
  const tier = user?.tier || "free";
  const subStatus = user?.subscription_status;
  // Show a Pro badge when tier resolves to "pro"; legacy escape-hatch users
  // come back as tier="pro" too (see auth.py /me handler).
  const isPro = tier === "pro";

  const onLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  const goSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <IngestionBanner />
      <header className="bg-ls-dark text-white">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <Link to="/" className="font-semibold tracking-tight text-lg flex items-baseline gap-2" data-testid="brand-mark">
            HCRIS<span className="text-ls-accent">+</span>
            <span className="text-[10px] font-mono text-zinc-400 uppercase tracking-widest">LiquidSMARTS</span>
          </Link>
          <form onSubmit={goSearch} className="flex-1 max-w-xl ml-6" data-testid="header-search-form">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, alias, or CCN…"
              className="w-full bg-zinc-800 text-white placeholder-zinc-500 border border-zinc-700 px-3 py-1.5 rounded-md text-sm font-mono outline-none focus:border-emerald-500"
              data-testid="header-search-input"
            />
          </form>
          <nav className="hidden md:flex items-center gap-4 text-sm text-zinc-300">
            <Link to="/" className="hover:text-white" data-testid="nav-search">Search</Link>
            <Link to="/reports" className="hover:text-white" data-testid="nav-reports">Reports</Link>
            <Link to="/billing" className="hover:text-white" data-testid="nav-billing">Billing</Link>
            {isAdmin && (
              <>
                <Link to="/admin/telemetry" className="hover:text-white" data-testid="nav-telemetry">Telemetry</Link>
                <Link to="/admin/data-quality" className="hover:text-white" data-testid="nav-data-quality">Data Quality</Link>
                <Link to="/admin/systems/add" className="hover:text-white" data-testid="nav-add-system">+ System</Link>
              </>
            )}
          </nav>
          <div className="flex items-center gap-3">
            <div className="text-right" data-testid="user-display">
              <div className="text-xs font-mono text-zinc-300 hidden sm:block">{userEmail}</div>
              <div className="hidden sm:flex items-center justify-end gap-1.5 mt-0.5">
                {isPro ? (
                  <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-px rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                        data-testid="user-tier-badge" title={subStatus ? `Pro · ${subStatus}` : "Pro"}>
                    Pro
                  </span>
                ) : (
                  <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-px rounded bg-zinc-700 text-zinc-300 border border-zinc-600"
                        data-testid="user-tier-badge" title="Free tier — upgrade at /billing">
                    Free
                  </span>
                )}
                {isAdmin && (
                  <span className="text-[9px] font-mono uppercase text-emerald-400 tracking-wider" data-testid="user-role">admin</span>
                )}
              </div>
            </div>
            <button onClick={onLogout} className="btn-ghost text-xs" data-testid="logout-button" style={{ background: "transparent", color: "#e4e4e7", border: "1px solid #3f3f46" }}>
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1">{children}</main>
      <footer className="border-t border-zinc-200 bg-zinc-50 px-4 py-4">
        <div className="max-w-7xl mx-auto text-[11px] font-mono text-zinc-500 flex flex-wrap items-center gap-x-6 gap-y-1">
          <span>HCRIS+ Phase 0 · POC</span>
          <span>Data: CMS HOSP10 (Form 2552-10)</span>
          <span>Every indicator carries worksheet/line/column lineage.</span>
          <span className="ml-auto">© LiquidSMARTS</span>
        </div>
      </footer>
    </div>
  );
}
